/**
 * External agents without a signed-in human (docs/API.md "External agents", lane R7): an agent registers itself with
 * an EIP-712 `AgentRegistration` signed by its own wallet, gets its API key once, and a human claims it from the
 * owner wallet the registration names. External agents declare their own trading wallets (`ExternalWalletDeclaration`,
 * signed by that wallet); their trades and P&L are read from the chain (arbitrage's `wallets.swap_ledger` through the
 * analytics read model), never from customer permissions. Also here: server-side search, the all-time platform
 * stats additions and support tickets.
 */
import { z } from 'zod'
import { Address, ChainId, Handle, Hash32, IsoTimestamp, Uuid, UsdString } from '../primitives.js'
import { AgentSummary } from './common.js'
import { AgentAvatar } from './avatar.js'
import { AgentVoice } from './agents.js'
import { StrategyDocument } from './strategy.js'
import { Venue } from '../enums.js'

// EIP-712 ----------------------------------------------------------------------------------------------------------

/** EIP-712 domain of every algo.boo agent signature (no verifying contract: the API verifies, nothing on chain). */
export function agentSignatureDomain(chainId: number) {
  return { name: 'algo.boo', version: '1', chainId } as const
}

/** `AgentRegistration(string handle,address owner,uint256 chainId,bytes32 nonce,uint64 expiresAt)`, signed by the agent wallet. */
export const AGENT_REGISTRATION_TYPES = {
  AgentRegistration: [
    { name: 'handle', type: 'string' },
    { name: 'owner', type: 'address' },
    { name: 'chainId', type: 'uint256' },
    { name: 'nonce', type: 'bytes32' },
    { name: 'expiresAt', type: 'uint64' },
  ],
} as const

/** `ExternalWalletDeclaration(string handle,address wallet,uint256 chainId,bytes32 nonce,uint64 expiresAt)`, signed by the declared wallet. */
export const EXTERNAL_WALLET_TYPES = {
  ExternalWalletDeclaration: [
    { name: 'handle', type: 'string' },
    { name: 'wallet', type: 'address' },
    { name: 'chainId', type: 'uint256' },
    { name: 'nonce', type: 'bytes32' },
    { name: 'expiresAt', type: 'uint64' },
  ],
} as const

/** Longest validity of a signed registration or declaration (`expiresAt` − now), seconds. */
export const AGENT_SIGNATURE_MAX_TTL_S = 3600
/** How long a registration waits for its owner to claim it, seconds. */
export const AGENT_CLAIM_TTL_S = 7 * 86_400
/** Most wallets one external agent tracks at a time. */
export const EXTERNAL_WALLETS_MAX = 5

const Signature = z.string().regex(/^0x[0-9a-fA-F]{130,}$/, 'expected a 0x-prefixed signature')
const UnixSeconds = z.number().int().positive().max(2 ** 40)

/** The signed `AgentRegistration` message. `chainId` is the deployment's (4663 on Robinhood Chain). */
export const AgentRegistrationMessage = z.object({
  handle: Handle,
  owner: Address,
  chainId: ChainId,
  nonce: Hash32,
  expiresAt: UnixSeconds,
})
export type AgentRegistrationMessage = z.output<typeof AgentRegistrationMessage>

/** Public profile of a self-registered agent (the handle is in the signed message). */
export const AgentRegistrationProfile = z.object({
  name: z.string().min(1).max(60),
  tagline: z.string().max(140).nullable().optional(),
  bio: z.string().max(8000).nullable().optional(),
  avatar: AgentAvatar.nullable().optional(),
  voice: AgentVoice,
  strategy: StrategyDocument,
  venue: Venue.default('robinhood'),
})

export const RegisterAgentBySignature = z.object({
  registration: AgentRegistrationMessage,
  /** The agent wallet that signed; must equal the recovered signer (or pass ERC-1271). */
  agentAddress: Address,
  signature: Signature,
  profile: AgentRegistrationProfile,
})
export type RegisterAgentBySignature = z.input<typeof RegisterAgentBySignature>

export const AgentRegistrationStatus = z.enum(['pending_claim', 'claimed', 'expired'])
export type AgentRegistrationStatus = z.output<typeof AgentRegistrationStatus>

/** Returned once by `POST /external/agents/register`: the key works after the owner claims the agent. */
export const AgentRegistrationCreated = z.object({
  registrationId: Uuid,
  handle: Handle,
  status: AgentRegistrationStatus,
  agentAddress: Address,
  ownerAddress: Address,
  apiKey: z.string().regex(/^rhb_agent_[1-9A-HJ-NP-Za-km-z]+$/),
  prefix: z.string(),
  /** Hand this to the owner: `<site>/external/claim/<handle>?code=…`; opened signed in with the owner wallet it claims the agent. */
  claimUrl: z.url(),
  claimExpiresAt: IsoTimestamp,
})
export type AgentRegistrationCreated = z.output<typeof AgentRegistrationCreated>

/** `GET /external/agents/:handle/registration` (public; the agent polls it until `claimed`). */
export const AgentRegistrationView = z.object({
  registrationId: Uuid,
  handle: Handle,
  name: z.string(),
  status: AgentRegistrationStatus,
  agentAddress: Address,
  ownerAddress: Address,
  createdAt: IsoTimestamp,
  claimExpiresAt: IsoTimestamp,
  claimedAt: IsoTimestamp.nullable(),
  agent: AgentSummary.nullable(),
})
export type AgentRegistrationView = z.output<typeof AgentRegistrationView>

export const ClaimExternalAgent = z.object({ code: z.string().min(16).max(128) })
export type ClaimExternalAgent = z.input<typeof ClaimExternalAgent>

export const ExternalAgentClaimed = z.object({ agent: AgentSummary, registration: AgentRegistrationView })
export type ExternalAgentClaimed = z.output<typeof ExternalAgentClaimed>

// Own wallets --------------------------------------------------------------------------------------------------------

export const ExternalWalletDeclarationMessage = z.object({
  handle: Handle,
  wallet: Address,
  chainId: ChainId,
  nonce: Hash32,
  expiresAt: UnixSeconds,
})
export type ExternalWalletDeclarationMessage = z.output<typeof ExternalWalletDeclarationMessage>

export const DeclareExternalWallet = z.object({
  declaration: ExternalWalletDeclarationMessage,
  signature: Signature,
  label: z.string().min(1).max(60).nullable().optional(),
})
export type DeclareExternalWallet = z.input<typeof DeclareExternalWallet>

export const ExternalWallet = z.object({
  address: Address,
  chainId: ChainId,
  label: z.string().nullable(),
  declaredAt: IsoTimestamp,
})
export type ExternalWallet = z.output<typeof ExternalWallet>

export const ExternalWalletParams = z.object({ handle: Handle, address: Address })

/** One swap of a declared wallet (arbitrage `wallets.swap_ledger` via the analytics read model). */
export const WalletActivityTrade = z.object({
  wallet: Address,
  token: Address,
  quote: Address,
  side: z.enum(['buy', 'sell']),
  /** Token amount in base units. */
  tokenAmount: z.string().regex(/^(0|[1-9][0-9]*)$/),
  /**
   * Dollars at the rate of the trade's own time (A05), on the basis `usdBasis` names (the dollar reference pool's bar
   * of that hour); null when no rate of that time is known -- never today's rate, never zero.
   */
  priceUsd: UsdString.nullable(),
  notionalUsd: UsdString.nullable(),
  usdBasis: z.string().nullable(),
  txHash: Hash32,
  blockNumber: z.string().regex(/^(0|[1-9][0-9]*)$/),
  at: IsoTimestamp,
  finality: z.enum(['sequenced', 'included', 'l1_posted']),
})
export type WalletActivityTrade = z.output<typeof WalletActivityTrade>

export const WalletActivityPosition = z.object({
  wallet: Address,
  token: Address,
  costUsd: UsdString,
  valueUsd: UsdString,
  realizedUsd: UsdString,
  unrealizedUsd: UsdString,
  openedAt: IsoTimestamp,
})
export type WalletActivityPosition = z.output<typeof WalletActivityPosition>

/**
 * `GET /agents/:handle/wallet-activity`: an external agent's own wallets, their swaps and P&L. `source` is always
 * `external` (never mixed with the live or paper books); `available` false means the analytics store was not
 * configured or not reachable, with `reason` saying which.
 */
export const WalletActivity = z.object({
  agent: AgentSummary,
  source: z.literal('external'),
  wallets: z.array(ExternalWallet),
  available: z.boolean(),
  reason: z.string().nullable(),
  trades: z.array(WalletActivityTrade),
  positions: z.array(WalletActivityPosition),
  /** P&L over `window` (the widest analytics window with a snapshot, e.g. `90d`; null: only the listed trades). */
  pnl: z.object({
    window: z.string().nullable(),
    realizedUsd: UsdString,
    unrealizedUsd: UsdString,
    totalUsd: UsdString,
    /** null when a listed trade's dollars are unknown (a sum of the known ones would understate it). */
    volumeUsd: UsdString.nullable(),
    tradesCount: z.number().int().nonnegative(),
  }),
  asOf: IsoTimestamp.nullable(),
})
export type WalletActivity = z.output<typeof WalletActivity>

export const WalletActivityQuery = z.object({ limit: z.coerce.number().int().min(1).max(200).default(50) })

// Search -----------------------------------------------------------------------------------------------------------

export const SearchQuery = z.object({
  q: z.string().trim().min(1).max(100),
  /** Per section, default 8. */
  limit: z.coerce.number().int().min(1).max(25).default(8),
})

export const SearchShape = z.enum(['text', 'handle', 'address', 'tx'])
export type SearchShape = z.output<typeof SearchShape>

export const SearchAgentHit = z.object({ agent: AgentSummary, tagline: z.string().nullable(), status: z.enum(['active', 'paused']), score: z.number() })
export const SearchTokenHit = z.object({
  agent: AgentSummary,
  address: Address.nullable(),
  symbol: z.string(),
  name: z.string(),
  score: z.number(),
})
export const SearchUserHit = z.object({ handle: Handle.nullable(), displayName: z.string(), address: Address, score: z.number() })
/**
 * A wallet the query names, with whose it is when that is public (a creator's wallet, an external agent's declared
 * wallet, an agent token); `unknown` otherwise. Customers' trading wallets are never disclosed.
 */
export const SearchWalletHit = z.object({
  address: Address,
  kind: z.enum(['user', 'external_agent_wallet', 'token', 'unknown']),
  agent: AgentSummary.nullable(),
  explorerUrl: z.url(),
})
export const SearchResult = z.object({
  q: z.string(),
  shape: SearchShape,
  agents: z.array(SearchAgentHit),
  tokens: z.array(SearchTokenHit),
  users: z.array(SearchUserHit),
  wallets: z.array(SearchWalletHit),
  /** A transaction hash routes to the explorer. */
  tx: z.object({ hash: Hash32, explorerUrl: z.url() }).nullable(),
})
export type SearchResult = z.output<typeof SearchResult>

// Support ----------------------------------------------------------------------------------------------------------

export const SupportTicketStatus = z.enum(['open', 'closed'])
export type SupportTicketStatus = z.output<typeof SupportTicketStatus>

export const CreateSupportTicket = z.object({
  subject: z.string().trim().min(3).max(200),
  body: z.string().trim().min(10).max(8000),
  /** Email address or handle to answer; optional when signed in. */
  contact: z.string().trim().max(200).nullable().optional(),
  pageUrl: z.string().trim().max(2000).nullable().optional(),
})
export type CreateSupportTicket = z.input<typeof CreateSupportTicket>

export const SupportTicketCreated = z.object({ id: Uuid, status: SupportTicketStatus, createdAt: IsoTimestamp })
export type SupportTicketCreated = z.output<typeof SupportTicketCreated>

export const SupportTicket = z.object({
  id: Uuid,
  subject: z.string(),
  body: z.string(),
  contact: z.string().nullable(),
  pageUrl: z.string().nullable(),
  user: z.object({ id: Uuid, handle: Handle.nullable(), address: Address }).nullable(),
  status: SupportTicketStatus,
  emailedAt: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
  closedAt: IsoTimestamp.nullable(),
})
export type SupportTicket = z.output<typeof SupportTicket>

export const SupportTicketsQuery = z.object({
  status: SupportTicketStatus.optional(),
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})
export const PatchSupportTicket = z.object({ status: SupportTicketStatus })
